import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import AIURLInput from '../../ui/inputs/AIURLInput';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import {
  setUserCompanyDetails,
  updateProfile,
} from '../../store/reducers/userSlice';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import {
  DETECT_CMS_API,
  SHOP_API,
} from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { submitLogToBugsnag } from '../../lib/api/log';
import '../../styles/components/sidebar/addNewShop.scss';
import ensureHttps from '../../lib/helper-functions/ensureHttps';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { fetchAppInit } from '../../lib/api/app-init';
import { createShop } from '../../lib/api/shop/create_shop';

interface AddNewShopProps {
  onBrandLimitReached?: () => void;
}

const AddNewShop = ({ onBrandLimitReached }: AddNewShopProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );

  const seller_id = useSelector(
    (state: RootState) => state.user.profile.seller_id
  );

  const seller_email = useSelector(
    (state: RootState) => state.user.profile.email
  );

  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);

  const taraTrialDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };

  const isActiveTrialWithDays =
    !!taraTrialDetails?.is_trial &&
    taraTrialDetails?.trial_available_days != null &&
    taraTrialDetails.trial_available_days > 0;

  const [loading, setLoading] = useState<'pending' | 'initial'>('initial');
  const [url, setURL] = useState('');
  const [isBrandLimitReached, setIsBrandLimitReached] = useState(false);
  const url_prefix = 'https://';

  async function detectCMS() {
    try {
      const sendingURL = ensureHttps(url);
      const requestInfo: RequestInfo = {
        url: DETECT_CMS_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder({ url: sendingURL }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${
            isWhiteLabel
              ? accessToken.currentWhiteLabelSellerToken
              : accessToken.sellerToken
          }`,
        },
      };

      const actionType = 'DETECT_CMS';
      const customMsg = 'CMS Detection Successful.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        false,
        true
      );

      const response = res.data;
      if (res.actionType === actionType && response?.data && response.success) {
        if (response?.data?.website_name) {
          return response?.data?.website_name;
        } else {
          setLoading('initial');
        }
      } else {
        setLoading('initial');
        return false;
      }
    } catch (exception) {
      setLoading('initial');
      console.log('Error during CMS detection request');
      submitLogToBugsnag('error', `${exception}`);
      return null;
    }
  }

  async function submitCompany(tempName: string) {
    try {
      setLoading('pending');
      const response = await createShop(tempName);

      if (response.success && response.data) {
        return {
          shop_slug: response.data.slug,
        };
      } else if (
        response.isLimitReached &&
        onBrandLimitReached &&
        !isActiveTrialWithDays
      ) {
        setIsBrandLimitReached(true);
        setLoading('initial');
        return { isLimitReached: true };
      }

      setLoading('initial');
      return false;
    } catch (exception) {
      setLoading('initial');
      console.log('Error during company creation request');
      submitLogToBugsnag('error', `${exception}`);
      return null;
    }
  }

  const submitWebshopData = async (companyResponse: any) => {
    const sendingURL = ensureHttps(url);
    const sendingData: any = {
      webshop_url: sendingURL,
    };

    const requestInfo: RequestInfo = {
      url: SHOP_API + `/${companyResponse?.shop_slug}/setting/shop-general`,
      method: 'PUT',
      body: applicationWWWXRLEncodedBodyBuilder(sendingData),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/x-www-form-urlencoded',
        Authorization: `${accessToken.type} ${
          isWhiteLabel
            ? accessToken.currentWhiteLabelSellerToken
            : accessToken.sellerToken
        }`,
      },
    };

    const actionType = 'ONBOARDING_WEB_SHOP_URL';
    const customMsg = t('Update Successfully.');

    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false,
      false,
      true
    );

    const response = res.data;

    if (res.actionType === actionType && response.success === true) {
      dispatch(
        setUserCompanyDetails({
          webshop_url: url_prefix + url,
        })
      );
      return true;
    } else {
      return false;
    }
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || !isNotEmptyString(url)) {
      return;
    }

    setLoading('pending');

    try {
      // const detectedCMSName = await detectCMS();
      // if (!detectedCMSName || typeof detectedCMSName !== 'string') {
      //   throw new Error('CMS detection failed');
      // }

      const tempName = `${seller_email}'s shop`;

      const companyCreated = await submitCompany(tempName);

      // Check if brand limit was reached (submitCompany returns special object)
      if (companyCreated && (companyCreated as any).isLimitReached) {
        // Brand limit was reached, state already updated, just return
        return;
      }

      if (!companyCreated) {
        throw new Error('Company creation failed');
      }

      if (isNotEmptyString(companyCreated?.shop_slug)) {
        const webshopDataSubmitted = await submitWebshopData(companyCreated);
        if (!webshopDataSubmitted) {
          throw new Error('Webshop data submission failed');
        } else {
          dispatch(
            updateProfile({
              active_shop_slug: companyCreated?.shop_slug,
            })
          );
          //* Fetch app init to update the state after switching shop
          fetchAppInit();
        }

        setLoading('initial');
      }
    } catch (error: any) {
      console.error('Error during submission:', error);

      // Check if error message indicates brand limit
      // This handles cases where the API call fails before reaching the response handler
      const errorMessage = error?.message || String(error) || '';
      const errorMessageLower = errorMessage.toLowerCase();
      const hasLimitError =
        errorMessageLower.includes('limit') ||
        errorMessageLower.includes('maximum') ||
        errorMessageLower.includes('exceeded') ||
        errorMessageLower.includes('brand limit') ||
        errorMessageLower.includes('shop limit');

      if (
        hasLimitError &&
        onBrandLimitReached &&
        !isActiveTrialWithDays
      ) {
        setIsBrandLimitReached(true);
      }

      setLoading('initial');
    }
  };

  const isDisabled = !url || !isNotEmptyString(url);

  const handleUpgradeClick = () => {
    if (onBrandLimitReached) {
      onBrandLimitReached();
    }
  };

  // Show brand limit reached message
  if (isBrandLimitReached) {
    return (
      <div className='addNewShop'>
        <div className='addNewShop__title'>
          <span>{t('AddNewShop')}</span>
        </div>
        <div className='addNewShop__limit-message'>
          <p>{t('BrandLimitReached')}</p>
        </div>
        <div className='addNewShop__button'>
          <MainSquareButton
            type={'button'}
            value={t('UpgradeToAddMoreBrands')}
            normal={true}
            onClick={handleUpgradeClick}
          />
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        submitForm(e);
      }}
      className='addNewShop'
    >
      <div className='addNewShop__title'>
        <span>{t('WebsiteURL')}</span>
      </div>
      <span className='addNewShop__description'>
        {t('OnboardingCompanyWebsiteURLDescription-Agency-1')}
      </span>

      <div className='addNewShop__input'>
        <AIURLInput
          prefix={url_prefix}
          changeListeners={[(e) => setURL(e.target.value)]}
          id='addNewShop__shop-url-input'
          value={url}
          label={t('')}
          placeholder={t('your-website.com')}
          title={t('URL-validation')}
          required={true}
        />
      </div>

      <div className='addNewShop__button'>
        <MainSquareButton
          type={'submit'}
          value={t('CreateAndSwitch')}
          normal={true}
          loading={loading === 'pending' ? true : false}
          disabled={isDisabled}
        />
      </div>
    </form>
  );
};

export default AddNewShop;
